import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useCart } from '../../context/CartContext';
import './EventsPage.css';
import { logError } from '../../utils/logger';

const EventsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { notify } = useNotifications();
  const { addToCart } = useCart();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(buildApiUrl('/api/products/events/public'), {
        headers,
      });

      if (!response.ok) throw new Error('Error al cargar eventos');
      const data = await response.json();
      setEvents(Array.isArray(data.events) ? data.events : []);
    } catch (error) {
      logError('[EventsPage] Error fetching events:', error);
      notify({
        message: 'Error al cargar los eventos',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const resolveImageUrl = (source) => {
    if (!source) return null;
    const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
    if (/^https?:\/\//i.test(sanitized)) return sanitized;
    const base = getApiBaseUrl();
    if (sanitized.startsWith('/')) return `${base}${sanitized}`;
    if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
    return `${base}/uploads/${sanitized}`;
  };

  // Obtener todas las fechas de eventos para el calendario
  const eventDates = useMemo(() => {
    const dates = new Set();
    events.forEach((event) => {
      if (event.diaseventos && Array.isArray(event.diaseventos)) {
        event.diaseventos.forEach((dia) => {
          if (dia.fecha) {
            const date = new Date(dia.fecha);
            dates.add(date.toISOString().split('T')[0]);
          }
        });
      }
    });
    return Array.from(dates);
  }, [events]);

  // Filtrar eventos por fecha seleccionada
  const filteredEvents = useMemo(() => {
    if (!selectedDate) return events;
    return events.filter((event) => {
      if (!event.diaseventos || !Array.isArray(event.diaseventos)) return false;
      return event.diaseventos.some((dia) => {
        const eventDate = new Date(dia.fecha);
        return eventDate.toISOString().split('T')[0] === selectedDate;
      });
    });
  }, [events, selectedDate]);

  // Funciones del calendario
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Días del mes anterior para completar la primera semana
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
      });
    }
    // Días del mes actual
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true,
      });
    }
    // Días del mes siguiente para completar la última semana
    const remainingDays = 42 - days.length; // 6 semanas * 7 días
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false,
      });
    }
    return days;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateHasEvent = (date) => {
    const dateStr = formatDate(date);
    return eventDates.includes(dateStr);
  };

  const handleDateClick = (date) => {
    if (!isDateHasEvent(date)) return;
    const dateStr = formatDate(date);
    setSelectedDate(selectedDate === dateStr ? null : dateStr);
  };

  const handleAddToCart = (event) => {
    if (!user || !token) {
      notify({
        message: 'Debes iniciar sesión para agregar eventos al carrito',
        type: 'info',
      });
      navigate('/login', { state: { from: '/events' } });
      return;
    }

    addToCart({
      id: event.id,
      name: event.tituloevento || event.name,
      price: event.price,
      type: 'event',
      image: event.imagenevento || event.image,
    });

    notify({
      message: 'Evento agregado al carrito',
      type: 'success',
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(price);
  };

  const formatTime = (time) => {
    if (!time) return '';
    if (typeof time === 'string') {
      const parts = time.split(':');
      return `${parts[0]}:${parts[1]}`;
    }
    return time;
  };

  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const calendarDays = getDaysInMonth(currentMonth);
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
    setSelectedDate(null);
  };

  return (
    <div className="events-page">
      {/* Hero Section with Calendar */}
      <section className="events-hero">
        <div className="events-hero-content">
          <div className="events-hero-text">
            <div className="events-hero-badge">
              <span>PRÓXIMOS EVENTOS</span>
            </div>
            <h1 className="events-hero-title">
              Eventos <span className="events-hero-title-accent">y Talleres</span>
            </h1>
            <p className="events-hero-description">
              Participa en nuestros <strong>eventos presenciales y virtuales</strong>. 
              Aprende de los mejores profesionales y expande tu red de contactos en eventos exclusivos.
            </p>
            <button 
              className="events-hero-button"
              onClick={() => {
                const calendarSection = document.querySelector('.events-calendar-section');
                if (calendarSection) {
                  calendarSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              VER EVENTOS
            </button>
          </div>
          <div className="events-hero-calendar">
            <div className="events-calendar-wrapper">
              <div className="events-calendar">
                <div className="calendar-header">
                  <button 
                    className="calendar-nav-button"
                    onClick={() => navigateMonth(-1)}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <h3 className="calendar-month-year">
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </h3>
                  <button 
                    className="calendar-nav-button"
                    onClick={() => navigateMonth(1)}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
                <div className="calendar-weekdays">
                  {weekDays.map((day) => (
                    <div key={day} className="calendar-weekday">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="calendar-days">
                  {calendarDays.map((day, index) => {
                    const dateStr = formatDate(day.date);
                    const hasEvent = isDateHasEvent(day.date);
                    const isSelected = selectedDate === dateStr;
                    const isToday = formatDate(new Date()) === dateStr;

                    return (
                      <div
                        key={index}
                        className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${
                          hasEvent ? 'has-event' : ''
                        } ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                        onClick={() => day.isCurrentMonth && handleDateClick(day.date)}
                      >
                        <span className="calendar-day-number">{day.date.getDate()}</span>
                        {hasEvent && <span className="calendar-day-dot"></span>}
                      </div>
                    );
                  })}
                </div>
              </div>
              {selectedDate && (
                <div className="calendar-selected-info">
                  <p className="selected-date-text">
                    <i className="fas fa-calendar-check me-2"></i>
                    Eventos del {formatEventDate(selectedDate)}
                  </p>
                  <button
                    className="clear-selection-btn"
                    onClick={() => setSelectedDate(null)}
                  >
                    <i className="fas fa-times me-1"></i>
                    Limpiar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Events Cards Section */}
      <section className="events-cards-section">
        <div className="events-cards-container">
          <h2 className="events-section-title">
            <i className="fas fa-list me-2"></i>
            {selectedDate ? `Eventos del ${formatEventDate(selectedDate)}` : 'Todos los Eventos'}
          </h2>
          {loading ? (
            <div className="events-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando eventos...</span>
              </div>
              <p>Cargando eventos...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="events-empty">
              <i className="fas fa-calendar-times"></i>
              <p>
                {selectedDate
                  ? 'No hay eventos programados para esta fecha'
                  : 'No hay eventos disponibles en este momento'}
              </p>
            </div>
          ) : (
            <div className="events-cards-list">
              {filteredEvents.map((event) => {
                const imageUrl = resolveImageUrl(event.imagenevento || event.image);
                const firstDay = event.diaseventos && Array.isArray(event.diaseventos) && event.diaseventos.length > 0
                  ? event.diaseventos[0]
                  : null;

                /* Detalle público: GET /api/products/public/event/:eventId usa referenceId (id del evento), no el id del producto */
                const detailEventId =
                  event.referenceId != null && event.referenceId !== ''
                    ? Number(event.referenceId)
                    : null;
                const canOpenDetail = Number.isFinite(detailEventId);

                return (
                  <div
                    key={event.id}
                    className={`event-card-horizontal${canOpenDetail ? ' event-card-horizontal--clickable' : ''}`}
                    role={canOpenDetail ? 'link' : undefined}
                    tabIndex={canOpenDetail ? 0 : undefined}
                    onClick={() => canOpenDetail && navigate(`/events/${detailEventId}`)}
                    onKeyDown={(e) => {
                      if (!canOpenDetail) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/events/${detailEventId}`);
                      }
                    }}
                  >
                    {imageUrl && (
                      <div className="event-card-image">
                        <img src={imageUrl} alt={event.tituloevento || event.name} />
                      </div>
                    )}
                    <div className="event-card-content">
                      <div className="event-card-header">
                        <h3 className="event-card-title">
                          {event.tituloevento || event.name}
                        </h3>
                        {event.formato && (
                          <span className={`event-format-badge ${event.formato === 'virtual' ? 'virtual' : 'presencial'}`}>
                            <i className={`fas fa-${event.formato === 'virtual' ? 'video' : 'map-marker-alt'}`}></i>
                            {event.formato === 'virtual' ? 'Virtual' : 'Presencial'}
                          </span>
                        )}
                      </div>
                      {event.descripcionbreve && (
                        <p className="event-card-description">{event.descripcionbreve}</p>
                      )}
                      <div className="event-card-details">
                        {firstDay && firstDay.fecha && (
                          <div className="event-detail-item">
                            <i className="fas fa-calendar"></i>
                            <span>{formatEventDate(firstDay.fecha)}</span>
                          </div>
                        )}
                        {firstDay && firstDay.horaInicio && (
                          <div className="event-detail-item">
                            <i className="fas fa-clock"></i>
                            <span>
                              {formatTime(firstDay.horaInicio)}
                              {firstDay.horaFin && ` - ${formatTime(firstDay.horaFin)}`}
                            </span>
                          </div>
                        )}
                        {event.formato === 'presencial' && event.direccion && (
                          <div className="event-detail-item">
                            <i className="fas fa-map-marker-alt"></i>
                            <span>{event.direccion}</span>
                          </div>
                        )}
                        {event.formato === 'virtual' && event.linkvirtual && (
                          <div className="event-detail-item">
                            <i className="fas fa-video"></i>
                            <span>Evento Virtual</span>
                          </div>
                        )}
                      </div>
                      <div className="event-cupos">
                        <i className="fas fa-users"></i>
                        <span>
                          {event.cuposdisponibles || event.cupos || 0} cupos disponibles
                        </span>
                      </div>
                      <div className="event-card-footer">
                        <div className="event-card-price-info">
                          <div className="event-price">
                            <span className="event-price-label">Precio:</span>
                            <span className="event-price-value">
                              {event.price === 0 ? 'Gratis' : formatPrice(event.price)}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="event-add-to-cart-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCart(event);
                          }}
                          disabled={!event.cuposdisponibles || event.cuposdisponibles === 0}
                        >
                          <i className="fas fa-shopping-cart me-2"></i>
                          Agregar al Carrito
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsPage;

